export type XlsxCellValue = string | number

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const MAX_COLUMN_WIDTH = 60

export class TableXlsxProvider {
	static readonly mimeType = XLSX_MIME

	/**
	 * Converte o texto da célula em número quando ele é claramente um número, para que o Excel
	 * permita somar, ordenar e formatar. Na dúvida, mantém o texto.
	 *
	 * - "1.234,56" / "1234,5" (pt-BR) e "1,234.56" / "1234.5" (en) viram número.
	 * - "1.234" e "12.345.678" são milhares (o Walter responde em pt-BR); "0.125" é decimal.
	 * - Códigos com zero à esquerda ("007"), unidades ("12 ha"), "%", "R$" e datas ficam texto.
	 */
	static parseCell(raw: string): XlsxCellValue {
		const text = (raw ?? '').trim()
		if (!/^-?\d[\d.,]*$/.test(text)) return raw ?? ''

		const unsigned = text.replace(/^-/, '')
		if (/^0\d/.test(unsigned)) return raw

		const lastComma = text.lastIndexOf(',')
		const lastDot = text.lastIndexOf('.')
		let normalized: string
		if (lastComma >= 0 && lastDot >= 0) {
			// Os dois separadores: o último é o decimal.
			normalized =
				lastComma > lastDot
					? text.replace(/\./g, '').replace(',', '.')
					: text.replace(/,/g, '')
			if (!this.hasValidGrouping(text, lastComma > lastDot ? '.' : ',')) return raw
		} else if (lastComma >= 0) {
			if ((text.match(/,/g) || []).length > 1) return raw
			normalized = text.replace(',', '.')
		} else if (lastDot >= 0) {
			if (/^-?[1-9]\d{0,2}(\.\d{3})+$/.test(text)) normalized = text.replace(/\./g, '')
			else if ((text.match(/\./g) || []).length > 1) return raw
			else normalized = text
		} else {
			normalized = text
		}

		const value = Number(normalized)
		return Number.isFinite(value) ? value : raw
	}

	/** Separador de milhar só vale em grupos de 3 dígitos: "1.234,5" sim, "12.34,5" não. */
	private static hasValidGrouping(text: string, thousandSep: '.' | ','): boolean {
		const decimalSep = thousandSep === '.' ? ',' : '.'
		const integerPart = text.replace(/^-/, '').split(decimalSep)[0]
		const groups = integerPart.split(thousandSep)
		return /^[1-9]\d{0,2}$/.test(groups[0]) && groups.slice(1).every((g) => /^\d{3}$/.test(g))
	}

	/**
	 * Gera o .xlsx da tabela: cabeçalho em negrito, congelado e com filtro, números como
	 * números e largura das colunas ajustada ao conteúdo.
	 *
	 * O exceljs é carregado sob demanda: só baixa o pacote quem de fato exporta.
	 */
	static async build(headers: string[], rows: string[][], sheetName = 'WalterIA'): Promise<ArrayBuffer> {
		const { default: ExcelJS } = await import('exceljs')
		const workbook = new ExcelJS.Workbook()
		const sheet = workbook.addWorksheet(sheetName, { views: [{ state: 'frozen', ySplit: 1 }] })

		sheet.addRow(headers)
		sheet.getRow(1).font = { bold: true }
		for (const row of rows) sheet.addRow(row.map((cell) => this.parseCell(cell)))

		if (headers.length) {
			sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: headers.length } }
		}
		headers.forEach((header, i) => {
			const longest = Math.max(header.length, ...rows.map((r) => String(r[i] ?? '').length))
			sheet.getColumn(i + 1).width = Math.min(longest + 2, MAX_COLUMN_WIDTH)
		})

		return (await workbook.xlsx.writeBuffer()) as ArrayBuffer
	}
}
